import React, { useContext, useCallback, useMemo } from 'react';
import {
  IObjektVy,
  IObjektVyComp,
  IBusinessCollectionProps,
} from '../../../../../../Types/ObjektVy';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDView from '../../../../../../BaseComponents/View/EDView';
import { isEmpty } from 'ramda';
import EDExpansionPanel from '../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import { capitalize } from '../../../../../../Utils/stringUtils';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AttestantTyp from './AttestantTyp/AttestantTyp';
import mergeSort from '../../../../../../Utils/mergeSort';
import { BusinessDataFormContext } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest,
} from '../Queries/_QueryTypes/FakturaDelQuery';
import {
  mergeBusinessDataFormValues,
  parseText,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaAttestSTATUSEnum,
} from '../../../../../../globalQueryTypes';
import { selectedId } from '../../../../../../Components/MasterDetail/MasterDetailUtils';
import EDAvenyProgress from '../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { sortFakturattestByAttestant } from '../Utils/FakturadelUtils';

export const AttestanterComp: IObjektVyComp = {
  caption: '',
  comp: (props: IBusinessCollectionProps) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formContext = useContext(BusinessDataFormContext);

    const mergedData = mergeBusinessDataFormValues(
      parseText(formContext?.data),
      parseText(formContext?.formState?.values ?? {})
    );
    const fakturadelarLista: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel[] =
      mergeSort(
        (mergedData?._Associations?.Faktura?._Associations
          ?.Fakturadel as FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel[]) ??
          [],
        (a, b) => {
          return (a.ORDNINGS_NR || 0) - (b.ORDNINGS_NR || 0);
        }
      );

    if (
      !formContext?.mutationLoading &&
      !formContext?.formState.loading &&
      isEmpty(fakturadelarLista)
    ) {
      return formContext?.error ? (
        <>Fel vid hämtning av fakturadelar</>
      ) : (
        <>Fakturadelar saknas</>
      );
    }

    if (formContext?.formState.loading && isEmpty(fakturadelarLista)) {
      return (
        <span
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '80px',
            position: 'relative',
            width: '100%',
          }}
        >
          <EDAvenyProgress />
        </span>
      );
    }

    return (
      <EDView flexContainer>
        {fakturadelarLista.map(fakturadel => (
          <FakturaDelensAttestanter
            key={fakturadel.id}
            aktuellAttestDel={
              selectedId(props.config?.parents?.[0].id) ?? undefined
            }
            fakturadel={fakturadel}
          />
        ))}
      </EDView>
    );
  },
};

interface IFakturaDelAttestanterProps {
  aktuellAttestDel?: string;
  allExpanded?: boolean;
  fakturadel: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel;
}

const getAttestStatusName = (attestStatus: string): string => {
  let status = '';

  switch (attestStatus) {
    case 'EJ_UTDELAD_FOR_ATTEST':
      status = attestStatus.replace('O', 'Ö');
      break;
    case 'EJ_UTDELAD_FOR_BESLUTSATTEST':
      status = attestStatus.replace('O', 'Ö');
      break;
    case 'PA_VAG_TILL_BETALNING':
      status = 'PÅ_VÄG_TILL_BETALNING';
      break;
    case 'OTILLRACKLIG_ATTEST':
      status = 'OTILLRÄCKLIG_ATTEST';
      break;
    case 'SPARRAD':
      status = 'SPÄRRAD';
      break;
    case '':
      status = 'ATTEST_STATUS_SAKNAS';
      break;

    default:
      status = attestStatus;
      break;
  }
  return capitalize(status.toLowerCase()).replaceAll('_', ' ');
};

const useStyles = makeStyles()((theme: Theme) => {
  return {
    textDisabledColor: { color: theme.palette.action.disabled },
    headerContainer: {
      width: '100%',
      position: 'relative',
      fontSize: '16px',
      paddingRight: 8,
      paddingLeft: 8,
    },
  };
});

const attestanter = (
  fakturadel: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel
): FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[] =>
  fakturadel?._Associations?.Fakturaattest ?? [];

const getAttestantLista = (
  fakturadel: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel,
  attestTyp: TEDLRFakturaAttestAttestTypEnum
) =>
  mergeSort<FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest>(
    attestanter(fakturadel).filter(
      attestant => attestant.AttestTyp === attestTyp
    ),
    sortFakturattestByAttestant
  );

export const FakturaDelensAttestanter: React.FC<
  React.PropsWithChildren<IFakturaDelAttestanterProps>
> = props => {
  // const { avenyCollectionMap } = useContext(ModelContext);
  const { classes } = useStyles();

  const attestTypDisabled = !(
    !props.aktuellAttestDel ||
    props.aktuellAttestDel === props.fakturadel.id ||
    props.allExpanded
  );

  const allaBeslutattestanterHarAttesterat = useCallback(
    (
      beslutAttestanterLista: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[]
    ) => {
      const result = beslutAttestanterLista.every(
        (
          attestant: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest
        ) => {
          return attestant.STATUS === TEDLRFakturaAttestSTATUSEnum.ATTESTERAD;
        }
      );
      return result;
    },
    []
  );

  const beslutAttestanterLista = getAttestantLista(
    props.fakturadel,
    TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
  );

  const harAllaBeslutAttestanterAttesterat = useMemo(
    () => allaBeslutattestanterHarAttesterat(beslutAttestanterLista),
    [allaBeslutattestanterHarAttesterat, beslutAttestanterLista]
  );

  const allaAttestantlistorna = (
    <>
      <AttestantTyp
        rubrik="Mottagningsattest"
        attestantLista={getAttestantLista(
          props.fakturadel,
          TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
        )}
        disabled={attestTypDisabled}
        fakturadelStatus={props.fakturadel.ATTEST_STATUS}
        allaBeslutAttestanterHarAtttesterat={harAllaBeslutAttestanterAttesterat}
      />
      <AttestantTyp
        rubrik="Beslutsattest"
        attestantLista={beslutAttestanterLista}
        disabled={attestTypDisabled}
        fakturadelStatus={props.fakturadel.ATTEST_STATUS}
        allaBeslutAttestanterHarAtttesterat={harAllaBeslutAttestanterAttesterat}
      />
      {getAttestantLista(
        props.fakturadel,
        TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
      ).length > 0 && (
        <AttestantTyp
          rubrik="Kontrollattest"
          attestantLista={getAttestantLista(
            props.fakturadel,
            TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
          )}
          disabled={attestTypDisabled}
          fakturadelStatus={props.fakturadel.ATTEST_STATUS}
          allaBeslutAttestanterHarAtttesterat={
            harAllaBeslutAttestanterAttesterat
          }
        />
      )}
    </>
  );

  if (props.fakturadel._Associations?.Faktura?.AntalFakturadelar !== 1) {
    const attestGroup =
      props.fakturadel._Associations?.Attestgrupp == null
        ? 'Attestgrupp saknas'
        : props.fakturadel._Associations?.Attestgrupp.BENAMNING;

    return (
      <EDExpansionPanel
        key={props.fakturadel.ORDNINGS_NR ?? ''}
        startExpanded={
          props.aktuellAttestDel === props.fakturadel.id || props.allExpanded
        }
        label={
          <EDView className={classes.headerContainer}>
            <EDTypography
              className={
                !props.aktuellAttestDel ||
                props.aktuellAttestDel === props.fakturadel.id ||
                props.allExpanded
                  ? ''
                  : classes.textDisabledColor
              }
              variant="body2"
              bold
            >
              {`${attestGroup}, ${
                props.fakturadel.Attestdel
              }: ${getAttestStatusName(props.fakturadel.ATTEST_STATUS ?? '')}`}
            </EDTypography>
          </EDView>
        }
      >
        {allaAttestantlistorna}
      </EDExpansionPanel>
    );
  } else {
    return allaAttestantlistorna;
  }
};
export const Attestanter: IObjektVy = {
  type: 'ObjektVy',
  id: 'Attestanter',
  info: {},
  def: () => AttestanterComp,
};

export default AttestanterComp;
